// Draw the background that a banner shows into a canvas, so the pixelation
// dots can read its tone. The background is a `.bg-transformer` subtree: a
// positioned picture or video (BiggerPicture: translate + scale, then rotate +
// flip), a baked cut, or both (the cut as an underlay). This module walks the
// layout and the computed CSS transforms of that subtree, so it draws what is
// on the screen for every kind, and it does not know the BiggerPicture props.
//
// The generative background is CSS gradients, not media, so it gives no
// pixels: rasterizeBackground then returns false.

/** Layout offset of `el` inside `parent`, px, without transforms. */
function offsetInParent(el: HTMLElement, parent: HTMLElement): [number, number] {
  if (el.offsetParent === parent) {
    return [el.offsetLeft + parent.clientLeft, el.offsetTop + parent.clientTop];
  }
  if (el.offsetParent && el.offsetParent === parent.offsetParent) {
    return [el.offsetLeft - parent.offsetLeft, el.offsetTop - parent.offsetTop];
  }
  return [0, 0];
}

/** The transform of one box into its parent's box: offset, then CSS transform. */
function localMatrix(el: HTMLElement, parent: HTMLElement): DOMMatrix {
  const [ox, oy] = offsetInParent(el, parent);
  const m = new DOMMatrix().translateSelf(ox, oy);
  const cs = getComputedStyle(el);
  if (cs.transform && cs.transform !== "none") {
    const [tx = 0, ty = 0] = cs.transformOrigin.split(" ").map(parseFloat);
    m.translateSelf(tx, ty).multiplySelf(new DOMMatrix(cs.transform)).translateSelf(-tx, -ty);
  }
  return m;
}

type Layer = {
  source: HTMLImageElement | HTMLVideoElement;
  /** The box the source fills (the img itself, or the inline-SVG span). */
  box: HTMLElement;
};

/** The media of a `.bg-transformer` in paint order, with the box each fills. */
function backgroundLayers(root: HTMLElement): Layer[] {
  const layers: Layer[] = [];
  for (const el of root.querySelectorAll<HTMLImageElement | HTMLVideoElement>("img, video")) {
    if (el.closest(".tools")) continue;
    // An inline SVG shows as markup; its <img> is a 1×1 helper with the same
    // source. Draw that source at the size of the SVG span.
    const svgSpan = el.parentElement?.closest<HTMLElement>("[data-bg-inline-svg]");
    layers.push({source: el, box: svgSpan && root.contains(svgSpan) ? svgSpan : el});
  }
  return layers;
}

function isReady(source: HTMLImageElement | HTMLVideoElement): boolean {
  if (source instanceof HTMLVideoElement) return source.readyState >= 2 && source.videoWidth > 0;
  return source.complete && source.naturalWidth > 0;
}

/** Opacity of `el` and its ancestors up to (without) `root`. */
function chainOpacity(el: HTMLElement, root: HTMLElement): number {
  let alpha = 1;
  for (let node: HTMLElement | null = el; node && node !== root; node = node.parentElement) {
    alpha *= Number(getComputedStyle(node).opacity);
  }
  return alpha;
}

/** The colour behind the background: the first painted ancestor colour. */
export function backdropColorOf(root: HTMLElement): string {
  const template = root.closest<HTMLElement>(".template");
  for (let node: HTMLElement | null = root; node; node = node.parentElement) {
    const color = getComputedStyle(node).backgroundColor;
    if (color && color !== "transparent" && !/^rgba\(.*,\s*0\)$/.test(color)) return color;
    if (node === template) break;
  }
  const base = template && getComputedStyle(template).getPropertyValue("--base-color").trim();
  return base || "#000";
}

export type RasterResult =
  | {ok: true; data: Uint8ClampedArray; width: number; height: number}
  | {ok: false; reason: "no-media" | "loading" | "tainted"};

/**
 * Draw the background of `bgRoot` (a `.bg-transformer`) as it shows inside
 * `target` (the overlay canvas element, `width` × `height` layout px), over
 * the colour behind it, at `scale` (sample px per layout px). Returns the RGBA
 * pixels, or why there are none: no media (generative), media still loading,
 * or a cross-origin image without CORS.
 */
export function rasterizeBackground(
  bgRoot: HTMLElement,
  target: HTMLElement,
  width: number,
  height: number,
  scale: number
): RasterResult {
  const layers = backgroundLayers(bgRoot);
  if (!layers.length) return {ok: false, reason: "no-media"};
  if (!layers.every((l) => isReady(l.source))) return {ok: false, reason: "loading"};

  const sw = Math.max(1, Math.round(width * scale));
  const sh = Math.max(1, Math.round(height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext("2d", {willReadFrequently: true});
  if (!ctx) throw new Error("rasterizeBackground: no 2D context");

  // Where the background box sits in the overlay, in layout px. Both boxes are
  // untransformed, so the screen rects differ only by the preview zoom.
  const targetRect = target.getBoundingClientRect();
  const rootRect = bgRoot.getBoundingClientRect();
  const zoom = targetRect.width / (target.offsetWidth || width) || 1;
  const rootX = (rootRect.left - targetRect.left) / zoom;
  const rootY = (rootRect.top - targetRect.top) / zoom;

  ctx.fillStyle = backdropColorOf(bgRoot);
  ctx.fillRect(0, 0, sw, sh);
  ctx.save();
  ctx.scale(scale, scale);
  ctx.translate(rootX, rootY);
  ctx.beginPath();
  ctx.rect(0, 0, bgRoot.offsetWidth, bgRoot.offsetHeight);
  ctx.clip();
  for (const {source, box} of layers) {
    // The opacity of the box, not of the source: the inline-SVG helper <img>
    // is transparent itself, but its SVG span shows.
    const alpha = chainOpacity(box, bgRoot);
    if (alpha <= 0) continue;
    let m = new DOMMatrix();
    const chain: HTMLElement[] = [];
    for (let node: HTMLElement | null = box; node && node !== bgRoot; node = node.parentElement) {
      chain.unshift(node);
    }
    let parent = bgRoot;
    for (const node of chain) {
      m = m.multiply(localMatrix(node, parent));
      parent = node;
    }
    const filter = getComputedStyle(box).filter;
    ctx.save();
    ctx.transform(m.a, m.b, m.c, m.d, m.e, m.f);
    ctx.globalAlpha = alpha;
    if (filter && filter !== "none" && "filter" in ctx) ctx.filter = filter;
    ctx.drawImage(source, 0, 0, box.offsetWidth, box.offsetHeight);
    ctx.restore();
  }
  ctx.restore();

  try {
    return {ok: true, data: ctx.getImageData(0, 0, sw, sh).data, width: sw, height: sh};
  } catch {
    return {ok: false, reason: "tainted"};
  }
}
